// components/AirportSearch.tsx

import { useState, useEffect, useRef } from 'react';
import { MetroOrigin, fetchMetroOrigins } from '../layers/fetchLayers';
import './AirportSearch.css';

interface AirportSearchProps {
  onSelect: (metro: MetroOrigin) => void;
  onClear: () => void;
  mapInstance?: any;
}

export default function AirportSearch({ onSelect, onClear, mapInstance }: AirportSearchProps) {
  const [query, setQuery] = useState('');
  const [allMetros, setAllMetros] = useState<MetroOrigin[]>([]);
  const [suggestions, setSuggestions] = useState<MetroOrigin[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // ── CHARGER TOUS LES MÉTROS AU MONTAGE ──
  useEffect(() => {
    const loadMetros = async () => {
      setLoading(true);
      try {
        const data = await fetchMetroOrigins();
        console.log('✅ Métros chargés:', data.length);
        setAllMetros(data);
      } catch (error) {
        console.error('❌ Erreur chargement métros:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMetros();
  }, []);

  // ── GESTION DU CLIC À L'EXTÉRIEUR ──
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── FILTRAGE DES SUGGESTIONS (dès 1 caractère) ──
  useEffect(() => {
    if (selected) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    // ← MODIFICATION ICI : 1 caractère minimum au lieu de 2
    if (query.length < 1) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const filtered = allMetros.filter(metro =>
      metro.metro_origin.toLowerCase().includes(query.toLowerCase())
    );
    
    console.log(`🔍 Recherche "${query}" → ${filtered.length} résultats`);
    setSuggestions(filtered.slice(0, 10));
    setIsOpen(filtered.length > 0);
  }, [query, allMetros, selected]);

  // ── QUAND ON SÉLECTIONNE UN MÉTRO ──
  const handleSelect = (metro: MetroOrigin) => {
    console.log('✅ Sélection:', metro.metro_origin);
    setQuery(metro.metro_origin);
    setSelected(true);
    setIsOpen(false);
    setSuggestions([]);
    onSelect(metro);
  };

  // ── QUAND ON EFFACE LA RECHERCHE ──
  const handleClear = () => {
    console.log('🗑️ Effacement recherche - retour à la vue initiale');
    setQuery('');
    setSelected(false);
    setSuggestions([]);
    setIsOpen(false);
    onClear();
  };

  const highlightMatch = (text: string, query: string) => {
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    return (
      <>
        {text.substring(0, index)}
        <span className="highlight">{text.substring(index, index + query.length)}</span>
        {text.substring(index + query.length)}
      </>
    );
  };

  return (
    <div className="airport-search-wrapper" ref={wrapperRef}>
      <div className="airport-search-input-wrapper">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Search city or metro area..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelected(false);
          }}
          className="airport-search-input"
        />
        {query && (
          <button className="airport-search-clear" onClick={handleClear}>
            ×
          </button>
        )}
        {loading && (
          <div className="airport-search-loader">
            <div className="loader-dot" />
          </div>
        )}
      </div>

      {isOpen && suggestions.length > 0 && !selected && (
        <ul className="airport-search-suggestions">
          {suggestions.map((metro) => (
            <li key={metro.metro_origin} onClick={() => handleSelect(metro)}>
              <div className="suggestion-main">
                <span className="suggestion-name">
                  {highlightMatch(metro.metro_origin, query)}
                </span>
                <span className="suggestion-code" style={{ 
                  background: 'rgba(0, 154, 132, 0.15)',
                  color: '#009A84',
                  fontSize: '9px'
                }}>
                  {metro.lat.toFixed(2)}, {metro.lng.toFixed(2)}
                </span>
              </div>
              <div className="suggestion-sub">
                <span>Metro area</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ← MODIFICATION ICI : message si aucun résultat avec 1 caractère ou plus */}
      {isOpen && query.length >= 1 && suggestions.length === 0 && !selected && (
        <div className="airport-search-noresult">No city or metro area found</div>
      )}
    </div>
  );
}